import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Save, Search, Sparkles, Users, Info } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const PRESENCES = ["present", "absent", "excuse"];
const EFFECTS = ["tres_positif", "positif", "neutre", "difficile", "refus"];
const LVL_COLOR = { forte: "#10b981", pertinente: "#0ea5e9", possible: "#94a3b8" };

// Bilan de participation nominatif dans le Planning — même enregistrement /pp/participation que la vue Recommandations.
// Deux sections d'ajout : (1) Prioritaires — objectif PP lié ; (2) Autres — potentiellement intéressés.
export default function BilanDialog({ open, onClose, catalogId, activityName, activityDate }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [maisonId, setMaisonId] = useState("");
  const [rows, setRows] = useState([]);
  const [part, setPart] = useState({});
  const [names, setNames] = useState({});
  const [matches, setMatches] = useState({});   // rid -> { level, objective_title, action, keywords }
  const [othersIds, setOthersIds] = useState([]); // rid[] sans objectif lié
  const [q, setQ] = useState("");
  const [denied, setDenied] = useState(false);

  useEffect(() => {
    if (!open || !catalogId) return;
    let alive = true;
    (async () => {
      setLoading(true); setDenied(false);
      try {
        const rec = await api.post("/pp/recommendations", { catalog_id: catalogId });
        const mid = rec.data.maison_id || "";
        if (!alive) return;
        setMaisonId(mid);
        // Cartographie du matching PP
        const mm = {};
        (rec.data.recommendations || []).forEach((r) => {
          const reason = r.reasons?.[0];
          mm[r.resident_id] = {
            level: r.level,
            objective_id: reason?.objective_id || null,
            objective_title: reason?.objective_title || "",
            domain: reason?.domain || "",
            action: reason?.actions?.[0] || "",
            keywords: reason?.matched_keywords || [],
          };
        });
        setMatches(mm);
        setOthersIds((rec.data.others || []).map((o) => o.resident_id));
        const [trk, pk, pt] = await Promise.all([
          api.get("/pp/tracking"),
          api.get("/pp/recommendations/picks", { params: { activity_key: catalogId, maison_id: mid } }),
          api.get("/pp/participation", { params: { activity_key: catalogId, maison_id: mid, activity_date: activityDate } }),
        ]);
        if (!alive) return;
        const nm = {}; (trk.data.rows || []).forEach((r) => { nm[r.id] = { name: r.full_name, room: r.room, maison_id: r.maison_id, status: r.status }; });
        setNames(nm);
        const retained = (pk.data.picks || []).filter((p) => p.decision === "add").map((p) => p.resident_id);
        const pmap = {}; (pt.data.participation || []).forEach((p) => { pmap[p.resident_id] = { presence: p.presence, effect: p.effect || "", note: p.note || "" }; });
        setPart(pmap);
        const ids = Array.from(new Set([...retained, ...Object.keys(pmap)])).filter((id) => nm[id]);
        setRows(ids);
      } catch (e) {
        if (e?.response?.status === 403) setDenied(true);
        else toast.error(formatApiError(e?.response?.data?.detail));
      }
      if (alive) setLoading(false);
    })();
    return () => { alive = false; };
  }, [open, catalogId, activityDate]);

  const qq = q.trim().toLowerCase();
  const nameMatch = (id) => !qq || (names[id]?.name || "").toLowerCase().includes(qq);

  // Prioritaires à ajouter (objectif PP lié, pas encore dans le bilan)
  const priorityToAdd = useMemo(() =>
    Object.keys(matches).filter((id) => names[id] && names[id].maison_id === maisonId && names[id].status !== "sorti" && !rows.includes(id) && nameMatch(id))
      .sort((a, b) => (({ forte: 3, pertinente: 2, possible: 1 })[matches[b].level] || 0) - (({ forte: 3, pertinente: 2, possible: 1 })[matches[a].level] || 0)),
  [matches, names, maisonId, rows, qq]); // eslint-disable-line react-hooks/exhaustive-deps

  // Autres (potentiellement intéressés)
  const othersToAdd = useMemo(() =>
    othersIds.filter((id) => names[id] && names[id].maison_id === maisonId && names[id].status !== "sorti" && !rows.includes(id) && nameMatch(id))
      .sort((a, b) => (names[a].name || "").localeCompare(names[b].name || "")),
  [othersIds, names, maisonId, rows, qq]); // eslint-disable-line react-hooks/exhaustive-deps

  const setP = (rid, patch) => setPart((p) => ({ ...p, [rid]: { ...(p[rid] || { presence: "present", effect: "", note: "" }), ...patch } }));
  const save = async (rid) => {
    const p = part[rid] || { presence: "present", effect: "", note: "" };
    try {
      await api.post("/pp/participation", {
        activity_key: catalogId, activity_name: activityName || "", activity_date: activityDate,
        maison_id: maisonId, resident_id: rid, resident_name: names[rid]?.name || "",
        presence: p.presence, effect: p.presence === "present" ? (p.effect || null) : null, note: p.note || "",
        linked_goal_id: matches[rid]?.objective_id || null,
      });
      toast.success(t("pp.bilan.saved"));
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const SourceBadge = ({ rid }) => {
    const m = matches[rid];
    if (m) {
      const color = LVL_COLOR[m.level] || LVL_COLOR.possible;
      return <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold inline-flex items-center gap-1" style={{ background: `${color}22`, color }}><Sparkles className="h-3 w-3" />{t("pp.reco.sourcePP")}</span>;
    }
    return <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold bg-slate-500/20 text-slate-300">{t("pp.reco.sourceManual")}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0f2130] border-white/10 text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="pl-bilan-dialog">
        <DialogHeader><DialogTitle>{t("pp.bilan.title")} — {activityName} · {activityDate}</DialogTitle></DialogHeader>
        {denied ? (
          <p className="text-sm text-slate-400 py-6">—</p>
        ) : loading ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-teal-400" /></div>
        ) : (
          <div className="space-y-3">
            {rows.length === 0 && <p className="text-xs text-slate-500">{t("pp.bilan.noResidents")}</p>}
            {rows.map((rid) => {
              const p = part[rid] || { presence: "present", effect: "", note: "" };
              const m = matches[rid];
              return (
                <div key={rid} className="rounded-lg bg-white/[0.03] border border-white/10 p-3" data-testid={`pl-bilan-row-${rid}`}>
                  <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
                    <span className="font-semibold text-sm inline-flex items-center gap-2">
                      {names[rid]?.name}{names[rid]?.room ? ` · ${names[rid].room}` : ""}
                      <SourceBadge rid={rid} />
                    </span>
                    <button onClick={() => save(rid)} data-testid={`pl-bilan-save-${rid}`} className="inline-flex items-center gap-1 rounded-full bg-teal-500 hover:bg-teal-400 text-slate-900 px-3 py-1 text-xs font-semibold shrink-0"><Save className="h-3.5 w-3.5" />{t("pp.bilan.save")}</button>
                  </div>
                  {m && (
                    <div className="text-[11px] text-teal-300/80 mb-2 -mt-1">
                      {t("pp.reco.objective")} : {m.objective_title}{m.action ? ` · ${t("pp.reco.actionLbl")} : ${m.action}` : ""}
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    <select value={p.presence} onChange={(e) => setP(rid, { presence: e.target.value })} className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm">
                      {PRESENCES.map((x) => <option key={x} value={x} className="bg-[#0f2130]">{t(`pp.bilan.${x}`)}</option>)}
                    </select>
                    <select value={p.effect} disabled={p.presence !== "present"} onChange={(e) => setP(rid, { effect: e.target.value })} className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm disabled:opacity-50">
                      <option value="" className="bg-[#0f2130]">{t("pp.bilan.effect")}…</option>
                      {EFFECTS.map((x) => <option key={x} value={x} className="bg-[#0f2130]">{t(`pp.bilan.effects.${x}`)}</option>)}
                    </select>
                    <input value={p.note || ""} onChange={(e) => setP(rid, { note: e.target.value })} placeholder={t("pp.bilan.note")} className="h-9 rounded-lg bg-white/[0.04] border border-white/10 px-2 text-sm" />
                  </div>
                </div>
              );
            })}

            {/* Ajout d'habitants — 2 sections */}
            <div className="border-t border-white/10 pt-3">
              <div className="relative mb-3">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("pp.reco.searchResident")} className="w-full h-9 rounded-lg bg-white/[0.04] border border-white/10 pl-9 pr-3 text-sm" data-testid="pl-bilan-search" />
              </div>

              {/* Section prioritaires */}
              <div className="mb-3" data-testid="pl-bilan-priority-section">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="h-2.5 w-2.5 rounded-full bg-emerald-500" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-400">{t("pp.reco.priorityGroup")}</span>
                  <span className="text-[11px] text-slate-500">({priorityToAdd.length})</span>
                </div>
                <div className="max-h-44 overflow-y-auto space-y-1">
                  {priorityToAdd.length === 0 && <p className="text-[11px] text-slate-500 pl-1">—</p>}
                  {priorityToAdd.map((id) => {
                    const m = matches[id]; const color = LVL_COLOR[m.level] || LVL_COLOR.possible;
                    return (
                      <button key={id} onClick={() => setRows((r) => [...r, id])} data-testid={`pl-bilan-add-priority-${id}`}
                        className="w-full text-left rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border-l-2 px-3 py-1.5" style={{ borderLeftColor: color }}>
                        <div className="text-sm text-slate-100 flex items-center gap-2">
                          + {names[id]?.name}{names[id]?.room ? ` · ${names[id].room}` : ""}
                          <span className="rounded-full px-1.5 py-0.5 text-[10px] font-semibold" style={{ background: `${color}22`, color }}>{t(`pp.reco.${m.level}`)}</span>
                        </div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{t("pp.reco.objective")} : {m.objective_title}{m.action ? ` · ${m.action}` : ""}</div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Section autres */}
              <div data-testid="pl-bilan-others-section">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-3.5 w-3.5 text-slate-400" />
                  <span className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{t("pp.reco.othersGroup")}</span>
                  <span className="text-[11px] text-slate-500">({othersToAdd.length})</span>
                </div>
                <p className="text-[11px] text-slate-500 mb-2 flex items-start gap-1.5"><Info className="h-3 w-3 mt-0.5 shrink-0" />{t("pp.reco.othersHint")}</p>
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {othersToAdd.length === 0 && <p className="text-[11px] text-slate-500 pl-1">—</p>}
                  {othersToAdd.map((id) => (
                    <button key={id} onClick={() => setRows((r) => [...r, id])} data-testid={`pl-bilan-add-other-${id}`}
                      className="w-full text-left rounded-lg bg-white/[0.03] hover:bg-white/[0.06] border-l-2 border-l-slate-500 px-3 py-1.5 text-sm text-slate-200">
                      + {names[id]?.name}{names[id]?.room ? ` · ${names[id].room}` : ""}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
